import React, { useCallback, useMemo, useRef, useState } from 'react';
import PratusTable from '../../../DesignSystems/PratusTable/PratusTable';
import { ColumnDef } from '@tanstack/react-table';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { SharedIcon } from '../../../SharedIcon/SharedIcon';
import { Material } from '../../hooks/useSafetyAnalysis';
import { v7 } from 'uuid';
import PratusInput from '../../../DesignSystems/New/PratusInput';

export type MaterialRow = Material

const emptyRow = (): MaterialRow => ({
  id: v7(),
  materialName: null,
  quantity: null,
  physState: null,
  niosh: null,
  specificGravity: null,
  ph: null,
  idlh: null,
  flashPoint: null,
  lel: null,
  uel: null,
});

const headerCell = (label: string) => (
  <span className="text-nowrap">{label}</span>
);
interface MaterialsTableProps {
  value: Material[] | null;
  onChange: (rows: MaterialRow[]) => void;
}
export default function MaterialsTable({
  value,
  onChange,
}: MaterialsTableProps) {
  const rows = useMemo(
    () => value ?? [],
    [value]
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const handleCellFocus = (rowId: string) => {
    setEditingRowId(rowId);
  };

  const handleCellBlur = () => {
    setEditingRowId(null);
  };

  const updateRowById = useCallback(
    (id: string, key: keyof MaterialRow, value: string | number) => {
      const updatedRows = rows.map((row) =>
        row.id === id ? { ...row, [key]: value } : row
      );
      onChange(updatedRows);
    },
    [onChange, rows]
  );

  const addRow = () => {
    const row = emptyRow();
    onChange([...rows, row]);
    setEditingRowId(row.id);
  };

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (editingRowId) return rows;
    if (!term) return rows;
    return rows?.filter(
      (r) =>
        r.id === editingRowId ||
        (r.materialName || '').toLowerCase().includes(term)
    );
  }, [rows, search, editingRowId]);

  const columns = useMemo<ColumnDef<MaterialRow, any>[]>(
    () => [
      {
        accessorKey: 'materialName',
        header: () => headerCell('Material'),
        cell: ({ row, getValue }) => (
          <PratusInput
            value={getValue<string>() ?? ''}
            onSave={(value) => {
              updateRowById(row.original.id, 'materialName', value);
            }}
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
            placeholder="Enter material"
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: true,
      },
      {
        accessorKey: 'quantity',
        header: () => headerCell('QTY'),
        cell: ({ row, getValue }) => (
          <PratusInput
            type="number"
            value={getValue<string>() ?? ''}
            onSave={(value) =>
              updateRowById(row.original.id, 'quantity', Number(value))
            }
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
            placeholder="Qty"
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
      {
        accessorKey: 'physState',
        header: () => headerCell('Phys State'),
        cell: ({ row, getValue }) => (
          <PratusInput
            value={getValue<string>() ?? ''}
            onSave={(value) =>
              updateRowById(row.original.id, 'physState', value)
            }
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
            placeholder="e.g., Solid/Liquid/Gas"
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
      {
        accessorKey: 'niosh',
        header: () => headerCell('NIOSH#'),
        cell: ({ row, getValue }) => (
          <PratusInput
            value={getValue<string>() ?? ''}
            onSave={(value) => updateRowById(row.original.id, 'niosh', value)}
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
            placeholder="#"
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
      {
        accessorKey: 'specificGravity',
        header: () => headerCell('Specific Gravity'),
        cell: ({ row, getValue }) => (
          <PratusInput
            value={getValue<string>() ?? ''}
            onSave={(value) =>
              updateRowById(row.original.id, 'specificGravity', value)
            }
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
      {
        accessorKey: 'ph',
        header: () => headerCell('pH'),
        cell: ({ row, getValue }) => (
          <PratusInput
            type="number"
            value={getValue<string>() ?? ''}
            onSave={(value) =>
              updateRowById(row.original.id, 'ph', Number(value))
            }
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
      {
        accessorKey: 'idlh',
        header: () => headerCell('IDLH'),
        cell: ({ row, getValue }) => (
          <PratusInput
            value={getValue<string>() ?? ''}
            onSave={(value) => updateRowById(row.original.id, 'idlh', value)}
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
      {
        accessorKey: 'flashPoint',
        header: () => headerCell('Flash Point'),
        cell: ({ row, getValue }) => (
          <PratusInput
            value={getValue<string>() ?? ''}
            onSave={(value) =>
              updateRowById(row.original.id, 'flashPoint', value)
            }
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
      {
        accessorKey: 'lel',
        header: () => headerCell('LEL'),
        cell: ({ row, getValue }) => (
          <PratusInput
            value={getValue<string>() ?? ''}
            onSave={(value) => updateRowById(row.original.id, 'lel', value)}
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
      {
        accessorKey: 'uel',
        header: () => headerCell('UEL'),
        cell: ({ row, getValue }) => (
          <PratusInput
            value={getValue<string>() ?? ''}
            onSave={(value) => updateRowById(row.original.id, 'uel', value)}
            onFocus={() => handleCellFocus(row.original.id)}
            onBlur={handleCellBlur}
          />
        ),
        meta: { headerAlign: 'left' },
        enableSorting: false,
      },
    ],
    [updateRowById]
  );

  return (
    <div ref={containerRef} className="materials-table">
      <div className="d-flex justify-content-between align-items-end mb-2 gap-4 flex-wrap">
        <div className="flex-grow-1" style={{ minWidth: 240 }}>
          <label className="form-label mb-1">
            Product or Material Description
          </label>
          <input
            className="form-control"
            placeholder="Search product/material..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <StylishNewButton className="button--primary" onClick={addRow}>
            <SharedIcon iconName="add" />
            Add Row
          </StylishNewButton>
        </div>
      </div>

      <PratusTable
        className="table-sm align-middle"
        headerClassName="table-dark"
        columns={columns}
        data={filteredRows ?? []}
        getRowId={(row) => row.id}
        noDataIndication={<span>No materials added</span>}
      />
    </div>
  );
}
